import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import ModalContainer from '@components/modals/ModalContainer';
import RangePlanTable from '@components/rangePlan/RangePlanTable';
import { ENTITY_TYPE } from '@components/rangePlan/rangePlanUtils';

import CollectionPresentation from '@features/collection/components/CollectionPresentation';
import StyleDraftBoard from '@features/collection/details/StyleDraftBoard';
import AddNewFolder from '@features/favourites/components/AddNewFolder';
import FavouriteDetailsTopFilter from '@features/favourites/components/FavouriteDetailsTopFilter';
import FavouriteDetailsTopHeader from '@features/favourites/components/FavouriteDetailsTopHeader';
import ProductFavourite from '@features/favourites/components/ProductFavourite';
import SidePanel from '@features/favourites/components/SidePanel';
import { FAVOURITES_DETAILS_TABS } from '@features/favourites/utils/favouriteUtil';
import {
	allFilterPages,
	collectionDetailsFieldList,
	getFilterParams
} from '@features/filter/static/filterUtil';
import FilterComponent from '@features/filter/v2/FilterComponent';

import { clearGlobalFilter } from '@store/actions/FilterAction';
import { closeLoader, openLoader } from '@store/actions/LoaderAction';
import {
	fetchFavouriteProducts,
	fetchTotalLikeCount,
	setActiveFavouritePageTab
} from '@store/favourite/actions';
import { getFavouriteProducts } from '@store/reducers';

import { getProductDetailsFilterFields } from '@services/collectionService';
import { setSystemUserPreferences } from '@services/commonService';
import {
	getDraftedProducts,
	getFolderById,
	removeFromDraft,
	undraftProduct
} from '@services/favouriteService';

import { getUserPreference, printApiError } from '@utils/Util';

import { LIST_VIEW_TYPE } from '@/constants/constant';

const FavouriteDetails = () => {
	const userPreference = getUserPreference();
	const [isLoading, setIsLoading] = useState(false);
	const [page, setPage] = useState(0);
	const [designList, setDesignList] = useState({
		data: [],
		totalPages: 0,
		totalElements: 0,
		currentPage: 0
	});
	const { id } = useParams();

	let allFavouritesForProducts = useSelector(getFavouriteProducts);
	const [defaultViewType, setDefaultViewType] = useState(
		userPreference?.FAVOURITE_VIEW_TYPE || 'LARGE_VIEW'
	);

	const [selectedProducts, setSelectedProducts] = useState([]);
	const [selectedSortingOption, setSelectedSortingOption] = useState(
		userPreference?.FAVOURITE_PRODUCT_SORTING_OPTION || { code: 'NEWEST', title: 'Newest first' }
	);
	const [isSearchBoxHidden, setIsSearchBoxHidden] = useState(true);
	const [isShowSidePanel, setIsShowSidePanel] = useState(false);
	const [isShowDraft, setIsShowDraft] = useState(false);
	const [isEditFolder, setIsEditFolder] = useState(false);
	const [folderInfo, setFolderInfo] = useState(null);
	const [selectedFolderToEdit, setSelectedFolderToEdit] = useState(null);
	const dispatch = useDispatch();
	const [isPlayPresentation, setIsPlayPresentation] = useState(false);
	const [draftedProducts, setDraftedProducts] = useState([]);
	const activeTab = useSelector((store) => store.favourite?.favouritePageTab);
	const filterFields = useSelector((store) => store?.filterFieldStore);
	const [showFilter, setShowFilter] = useState(false);

	const SIZE = 30;

	useEffect(() => {
		if (isFilterFromSidePanel()) return;
		dispatch(
			fetchFavouriteProducts(
				0,
				SIZE,
				'',
				selectedSortingOption?.code,
				id,
				false,
				createFilterParams(),
				defaultViewType === LIST_VIEW_TYPE.LIST_VIEW
			)
		);
	}, [selectedSortingOption?.code, activeTab, defaultViewType, filterFields]);

	useEffect(() => {
		if (showFilter) {
			const { filterParams } = getFilterParams(getFilterFieldFromStore(), id, 'folderId');
			dispatch(getProductDetailsFilterFields({ ...filterParams }));
		}
	}, [filterFields, showFilter]);

	useEffect(() => {
		// Clear global filter when component unmounts
		return () => {
			dispatch(clearGlobalFilter());
		};
	}, []);

	const getFilterFieldFromStore = () => {
		return parseInt(id) === -1 ? filterFields : filterFields?.DETAILS_PAGE_FILTERS;
	};

	const createFilterParams = () => {
		const { params } = getFilterParams(getFilterFieldFromStore(), id);
		return params;
	};

	useEffect(() => {
		if (designList?.data?.length) {
			dispatch(fetchTotalLikeCount());
		}
	}, [designList?.data?.length]);

	const isFilterFromSidePanel = () => {
		const referenceNumber = filterFields?.DETAILS_PAGE_FILTERS?.referenceNumber;
		if (!referenceNumber?.length) return false;
		return referenceNumber[0]?.isAsyncSearch === true;
	};

	const fetchDraftedProducts = async () => {
		try {
			setIsLoading(true);
			const response = await getDraftedProducts({
				page: 0,
				size: 1000,
				sortingOption: selectedSortingOption?.code,
				search: '',
				folderId: id,
				isDrafted: true,
				isListView: false
			});

			const draftedData =
				response?.data?.map((item) => ({
					...item?.productResponse
				})) || [];

			setDraftedProducts(draftedData);
		} catch (error) {
			printApiError(error);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		fetchDraftedProducts();
	}, [isShowDraft]);

	const handleDefaultViewType = async (viewType) => {
		const body = {
			...userPreference,
			FAVOURITE_VIEW_TYPE: viewType
		};
		setDefaultViewType(viewType);
		await setSystemUserPreferences(body);
	};

	const onChangeAllCheckAndSearchForStyle = async (e) => {
		let { name, value, checked } = e.target;
		if (name === 'allCheckBoxForStyle') {
			setSelectedProducts(checked ? designList?.data?.map((item) => item.id) : []);
		} else if (name === 'searchForStyle') {
			setIsLoading(true);
			dispatch(
				fetchFavouriteProducts(
					0,
					SIZE,
					value,
					selectedSortingOption?.code,
					id,
					false,
					createFilterParams(),
					defaultViewType === LIST_VIEW_TYPE.LIST_VIEW
				)
			).finally(() => {
				setIsLoading(false);
			});
		}
	};

	useEffect(() => {
		if (id) {
			fetchFolderById();
		}
	}, [id]);

	const fetchFolderById = async () => {
		try {
			setIsLoading(true);
			const response = await getFolderById(`?folderId=${id}`);
			setFolderInfo(response);
		} catch (error) {
			printApiError(error);
		} finally {
			setIsLoading(false);
		}
	};

	const handleEditFolder = (folder) => {
		setIsEditFolder(true);
		setSelectedFolderToEdit(folder);
	};

	const handleCloseModal = () => {
		setIsShowSidePanel(false);
		setIsEditFolder(false);
		setSelectedFolderToEdit(null);
	};

	const handleUndraftProduct = async (design) => {
		try {
			dispatch(openLoader());
			await removeFromDraft(design?.id, id);
			fetchDraftedProducts();
			dispatch(
				fetchFavouriteProducts(
					0,
					SIZE,
					'',
					selectedSortingOption?.code,
					id,
					false,
					[],
					defaultViewType === LIST_VIEW_TYPE.LIST_VIEW
				)
			);
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}
	};

	const handleUnLikeProduct = async (productId) => {
		try {
			dispatch(openLoader());
			await undraftProduct(productId, id);
			fetchDraftedProducts();
			dispatch(fetchTotalLikeCount());
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}

		setDraftedProducts(draftedProducts.filter((item) => parseInt(item?.id) !== parseInt(productId)));
	};

	const handleSortingOptionChange = async (option) => {
		setSelectedSortingOption(option);
		const body = {
			...userPreference,
			FAVOURITE_PRODUCT_SORTING_OPTION: option
		};
		await setSystemUserPreferences(body);
	};

	return (
		<>
			<FavouriteDetailsTopHeader onEditFolder={handleEditFolder} folderInfo={folderInfo} folderId={id}>
				<div className="navigation-tab shadow-nav-4dp bg-white">
					<ul>
						{FAVOURITES_DETAILS_TABS.map((item, index) => (
							<li
								key={`${item.code}-${index}`}
								className={`${activeTab.code === item.code ? 'active' : ''}`}
								onClick={() => dispatch(setActiveFavouritePageTab(item))}
							>
								{item.title}
							</li>
						))}
					</ul>
				</div>
				<div>
					{activeTab.code !== 'RANGE_PLAN' && (
						<>
							<FavouriteDetailsTopFilter
								allFavouritesForProducts={allFavouritesForProducts}
								isSearchBoxHidden={isSearchBoxHidden}
								setIsSearchBoxHidden={setIsSearchBoxHidden}
								onChangeAllCheckAndSearchForStyle={onChangeAllCheckAndSearchForStyle}
								defaultViewType={defaultViewType}
								handleDefaultViewType={handleDefaultViewType}
								setIsPlayPresentation={setIsPlayPresentation}
								onSelectOption={handleSortingOptionChange}
								setPage={setPage}
								selectedSortingOption={selectedSortingOption}
								setIsShowSidePanel={setIsShowSidePanel}
								setIsShowDraft={setIsShowDraft}
								draftedProducts={draftedProducts}
								id={id}
								designList={designList}
								getCategoryIdList={createFilterParams}
								setShowFilter={setShowFilter}
							/>
						</>
					)}
				</div>
				{isShowSidePanel && (
					<SidePanel
						setIsShowSidePanel={setIsShowSidePanel}
						selectedSortingOption={selectedSortingOption}
						fetchFavouriteProducts={fetchFavouriteProducts}
						viewType={defaultViewType}
						setProductList={setDesignList}
						productList={designList}
						handleSortingOptionChange={handleSortingOptionChange}
					/>
				)}
			</FavouriteDetailsTopHeader>
			<section className="bg-white filter-data-container filter-v3-data-container bg-white py-0">
				{activeTab.code === 'DESIGN' && (
					<FilterComponent
						filterPageName={allFilterPages.FAVOURITE_DETAILS}
						fields={collectionDetailsFieldList}
						showFilter={showFilter}
						isDependentFilter={true}
						className="favourite-details-filter-bar"
					/>
				)}
				{activeTab.code === 'DESIGN' ? (
					<div className="data-table">
						<ProductFavourite
							isLoading={isLoading}
							setIsLoading={setIsLoading}
							designList={designList}
							setDesignList={setDesignList}
							selectedProducts={selectedProducts}
							setSelectedProducts={setSelectedProducts}
							defaultViewType={defaultViewType}
							allFavourites={allFavouritesForProducts}
							onChangeAllCheck={onChangeAllCheckAndSearchForStyle}
							selectedSortingOption={selectedSortingOption}
							page={page}
							setPage={setPage}
							folderId={id}
							folderInfo={folderInfo}
							fetchDraftedProducts={fetchDraftedProducts}
							getCategoryIdList={createFilterParams}
							brandId={folderInfo?.brandResponse?.id}
							createFilterParams={createFilterParams}
						/>
					</div>
				) : (
					<div className="data-table mw-100">
						<RangePlanTable
							entityType={ENTITY_TYPE.FAVOURITE_FOLDER}
							brandName={folderInfo?.brandResponse?.name}
							brandId={folderInfo?.brandResponse?.id}
						/>
					</div>
				)}
			</section>
			<div className="collection-details-container">
				{isShowDraft && (
					<StyleDraftBoard
						onClose={() => setIsShowDraft(false)}
						totalDraft={draftedProducts?.length}
						unDraftProduct={handleUndraftProduct}
						products={draftedProducts}
						parentComponent="FAVOURITE_DETAILS"
						onUnLike={handleUnLikeProduct}
					/>
				)}
			</div>

			{isEditFolder && (
				<ModalContainer className="sample-location-modal">
					<AddNewFolder
						onCloseModal={() => handleCloseModal()}
						isEdit={isEditFolder}
						selectedFolderToEdit={selectedFolderToEdit}
						handleSuccess={fetchFolderById}
					/>
				</ModalContainer>
			)}

			<CollectionPresentation
				isPlay={isPlayPresentation}
				collectionName={folderInfo?.name}
				collectionId={id}
				onCLoseModal={() => setIsPlayPresentation(false)}
				entityType="FAVOURITE_FOLDER"
				selectedProductIds={selectedProducts}
			/>
		</>
	);
};

export default FavouriteDetails;
